import { useEffect, useState } from 'react';

import { DESKTOP_MIN_WIDTH, TABLET_MIN_WIDTH } from '../platform/types';

export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';

export interface BreakpointInfo {
  width: number;
  height: number;
  bp: Breakpoint;
  isTablet: boolean;
  isPhone: boolean;
  isDesktop: boolean;
}

const XL_MIN_WIDTH = 1280;

function snapshot(): BreakpointInfo {
  const width = typeof window === 'undefined' ? DESKTOP_MIN_WIDTH : window.innerWidth;
  const height = typeof window === 'undefined' ? 0 : window.innerHeight;

  let bp: Breakpoint = 'sm';
  if (width >= XL_MIN_WIDTH) bp = 'xl';
  else if (width >= DESKTOP_MIN_WIDTH) bp = 'lg';
  else if (width >= TABLET_MIN_WIDTH) bp = 'md';

  return {
    width,
    height,
    bp,
    isTablet: width >= TABLET_MIN_WIDTH && width < DESKTOP_MIN_WIDTH,
    isPhone: width < TABLET_MIN_WIDTH,
    isDesktop: width >= DESKTOP_MIN_WIDTH,
  };
}

/**
 * Web useBreakpoint — subscribes to window resize and returns the current
 * breakpoint. Per-pixel resizes are deduped: state only updates when `bp`
 * changes, so callers don't re-render on every resize tick.
 */
export function useBreakpoint(): BreakpointInfo {
  const [info, setInfo] = useState<BreakpointInfo>(() => snapshot());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => {
      const next = snapshot();
      setInfo((prev) => (prev.bp === next.bp ? prev : next));
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return info;
}
